import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../util/classes.ts';
import { Spinner } from './spinner.tsx';

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
    ({ className, onScroll, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                'relative w-full overflow-x-auto rounded-md border border-neutral-100 bg-white',
                className,
            )}
            onScroll={onScroll}
        >
            <table className={cn('w-full caption-bottom divide-y overflow-x-auto')} {...props} />
        </div>
    ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <thead ref={ref} className={cn('sticky top-0 z-20 [&_tr]:border-b', className)} {...props} />
    ),
);
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <>
            <tbody ref={ref} className={cn('[&_tr:last-child]:border-none', className)} {...props} />
        </>
    ),
);
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <tfoot
            ref={ref}
            className={cn('bg-primary text-primary-foreground font-medium', className)}
            {...props}
        />
    ),
);
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
    ({ className, onClick, ...props }, ref) => (
        <tr
            ref={ref}
            className={cn(
                'h-14',
                'group border-b border-neutral-100 transition-colors hover:bg-neutral-50/50 data-[state=selected]:bg-neutral-50',
                { 'hover:bg-neutral-50 cursor-pointer': !!onClick },
                className,
            )}
            onClick={onClick}
            {...props}
        />
    ),
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
    HTMLTableCellElement,
    React.ThHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'center' | 'right' }
>(({ className, align = 'left', ...props }, ref) => (
    <th
        ref={ref}
        className={cn(
            'bg-neutral-25 px-3 align-middle text-neutral-600 [&:has([role=checkbox])]:pr-0 text-sm font-normal',
            {
                'text-left': align === 'left',
                'text-center': align === 'center',
                'text-right': align === 'right',
            },
            className,
        )}
        {...props}
    />
));

TableHead.displayName = 'TableHead';

function TableCell({
    className,
    align = 'left',
    highlightOnHover = true,
    ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
    highlightOnHover?: boolean;
    align?: 'left' | 'center' | 'right';
}) {
    return (
        <td
            className={cn(
                'text-sm font-normal text-neutral-800',
                'px-3 align-middle [&:has([role=checkbox])]:pr-0',
                highlightOnHover && 'group-hover:bg-neutral-25',
                {
                    'text-left': align === 'left',
                    'text-center': align === 'center',
                    'text-right': align === 'right',
                },
                className,
            )}
            {...props}
        />
    );
}

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
    ({ className, ...props }, ref) => (
        <caption ref={ref} className={cn('text-muted-foreground mt-4 text-base', className)} {...props} />
    ),
);
TableCaption.displayName = 'TableCaption';

function TableCellLoading({
    colSpan,
    className,
    ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'center' | 'right' }) {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} className={cn('h-96 bg-white! text-center', className)} {...props}>
                <span className="flex justify-center">
                    <Spinner />
                </span>
            </TableCell>
        </TableRow>
    );
}

const TableCellEmpty = React.forwardRef<
    HTMLTableCellElement,
    React.TdHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'center' | 'right' }
>(({ colSpan, className, children, ...props }) => (
    <TableRow>
        <TableCell colSpan={colSpan} className={cn('h-96 bg-white! text-center', className)} {...props}>
            <span>{children}</span>
        </TableCell>
    </TableRow>
));
TableCellEmpty.displayName = 'TableCellEmpty';

export type TableSortDirection = 'asc' | 'desc';

export type TableSortHeaderProps = {
    title: React.ReactNode;
    direction: TableSortDirection | undefined;
    onDirectionChange: (direction: TableSortDirection) => void;
    align?: 'left' | 'center' | 'right';
    className?: string;
    children?: React.ReactNode;
};

function TableSortHeader({
    align = 'left',
    children,
    className,
    direction,
    onDirectionChange,
    title,
}: TableSortHeaderProps): React.ReactElement {
    const isSelected = direction !== undefined;

    const toggleDirection = (): void => {
        onDirectionChange(direction === 'asc' ? 'desc' : 'asc');
    };

    const indicator =
        direction === 'desc' ? (
            <ArrowDownIcon className="h-3 w-3 text-neutral-800" />
        ) : direction === 'asc' ? (
            <ArrowUpIcon className="h-3 w-3 text-neutral-800" />
        ) : (
            <svg className="h-2.5 w-2.5 text-neutral-300" fill="none" viewBox="0 0 8 10">
                <path
                    fill="currentColor"
                    stroke="currentColor"
                    strokeLinejoin="round"
                    strokeWidth="0.3"
                    d="M4.231.5L1.454 3.875h5.554L4.23.5zM4.231 9.5l2.777-3.375H1.454L4.231 9.5z"
                />
            </svg>
        );

    return (
        <div
            className={cn(
                'flex items-center',
                align === 'left' && '-ml-2 justify-start text-left',
                align === 'center' && 'justify-center text-center',
                align === 'right' && '-mr-2 justify-end text-right',
                className,
            )}
        >
            <div className="inline-flex items-center rounded-md hover:bg-neutral-100">
                <button
                    className={cn(
                        'inline-flex h-8 cursor-pointer items-center rounded-md px-2 text-sm font-normal whitespace-nowrap outline-hidden',
                        isSelected ? 'text-neutral-800' : 'text-neutral-600',
                    )}
                    onClick={toggleDirection}
                    type="button"
                >
                    <span>{title}</span>
                </button>
                {children}
                <button
                    aria-hidden
                    className="flex h-8 cursor-pointer items-center pr-2 pl-1"
                    onClick={toggleDirection}
                    tabIndex={-1}
                    type="button"
                >
                    {indicator}
                </button>
            </div>
        </div>
    );
}

export {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableCellEmpty,
    TableCellLoading,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
    TableSortHeader,
};
